import { useMemo } from "react";
import { searchEverything, type SearchHit } from "@/api/search";
import { CommandPalette, type IconName, type PaletteGroup, type PaletteItem } from "@/components";
import { collectPages } from "@/lib/routing";
import { featureRoutes } from "./featureRoutes";
import { navItemFor } from "./navigation";
import { THEMES } from "./theme/themes";
import { useThemeSwitcher } from "./theme/useThemeSwitcher";
import { useSignOut } from "./useSignOut";

interface HitKind {
  label: string;
  icon: IconName;
  path: (hit: SearchHit) => string | null;
}

/** How /api/search groups map to pages; unknown groups need a `url` on each hit. */
const SEARCH_GROUPS: Record<string, HitKind> = {
  materials: { label: "Materials", icon: "book", path: (hit) => `/materials/${encodeURIComponent(hit.id)}` },
  lots: { label: "Lots", icon: "box", path: (hit) => `/lots/${encodeURIComponent(hit.code ?? hit.id)}` },
  formulas: { label: "Formulas", icon: "flask", path: (hit) => `/formulas/${encodeURIComponent(hit.id)}` },
  locations: { label: "Locations", icon: "box", path: () => "/inventory/locations" },
};

function hitLabel(hit: SearchHit): string {
  return hit.label ?? hit.name ?? hit.title ?? hit.code ?? hit.id;
}

function hitDetail(hit: SearchHit): string | undefined {
  if (hit.detail) return hit.detail;
  const parts = [hit.code && hit.code !== hitLabel(hit) ? hit.code : null, hit.cas ? `CAS ${hit.cas}` : null].filter(
    Boolean,
  );
  return parts.length ? parts.join(", ") : undefined;
}

export async function searchPaletteGroups(query: string, signal: AbortSignal): Promise<PaletteGroup[]> {
  const response = await searchEverything(query, signal);
  return Object.entries(response.results ?? {}).map(([key, hits]) => {
    const kind = SEARCH_GROUPS[key];
    const items: PaletteItem[] = [];
    for (const hit of hits ?? []) {
      const to = hit.url ?? kind?.path(hit) ?? null;
      if (!to) continue;
      items.push({
        id: `${key}:${hit.id}`,
        label: hitLabel(hit),
        detail: hitDetail(hit),
        icon: kind?.icon ?? "file",
        to,
      });
    }
    return { id: key, label: kind?.label ?? key.charAt(0).toUpperCase() + key.slice(1), items };
  });
}

/** The Cmd/Ctrl+K palette with a command for every page, theme switching and sign-out. */
export function AppCommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { choose } = useThemeSwitcher();
  const signOut = useSignOut();

  const commands = useMemo<PaletteItem[]>(() => {
    const pages = collectPages(featureRoutes).map((page) => {
      const section = navItemFor(page.path);
      return {
        id: `page:${page.path}`,
        label: page.title,
        detail: section?.group.label,
        icon: section?.item.icon ?? "file",
        keywords: page.keywords,
        to: page.path,
      } satisfies PaletteItem;
    });
    const themes: PaletteItem[] = [
      ...THEMES.map((theme) => ({
        id: `theme:${theme.id}`,
        label: `Use ${theme.label.toLowerCase()} theme`,
        icon: theme.icon,
        keywords: ["theme", "appearance", "color", "mode"],
        onSelect: () => choose(theme.id),
      })),
      {
        id: "theme:system",
        label: "Match system theme",
        icon: "monitor",
        keywords: ["theme", "appearance", "auto", "dark", "light"],
        onSelect: () => choose("system"),
      },
    ];
    return [
      ...pages,
      ...themes,
      {
        id: "sign-out",
        label: "Sign out",
        icon: "logOut",
        keywords: ["log out", "logout"],
        onSelect: () => void signOut(),
      },
    ];
  }, [choose, signOut]);

  return <CommandPalette open={open} onOpenChange={onOpenChange} commands={commands} search={searchPaletteGroups} />;
}
